"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useShareDialogBase = void 0;
var tslib_1 = require("tslib");
// (C) 2021 GoodData Corporation
var react_1 = require("react");
var sdk_model_1 = require("@gooddata/sdk-model");
var types_1 = require("./types");
var utils_1 = require("./utils");
var useGetAccessList_1 = require("./backend/useGetAccessList");
var shareDialogMappers_1 = require("../shareDialogMappers");
/**
 * @internal
 */
var useShareDialogState = function (isUnderLenientControl, isLocked) {
    var _a = tslib_1.__read(react_1.useState("ShareGrantee"), 2), dialogMode = _a[0], setDialogMode = _a[1];
    var _b = tslib_1.__read(react_1.useState(true), 2), isGranteesLoading = _b[0], setIsGranteesLoading = _b[1];
    var _c = tslib_1.__read(react_1.useState([]), 2), grantees = _c[0], setGrantees = _c[1];
    var _d = tslib_1.__read(react_1.useState([]), 2), granteesToAdd = _d[0], setGranteesToAdd = _d[1];
    var _e = tslib_1.__read(react_1.useState([]), 2), granteesToDelete = _e[0], setGranteesToDelete = _e[1];
    var _f = tslib_1.__read(react_1.useState(isUnderLenientControl), 2), isUnderLenientControlNow = _f[0], setUnderLenientControlNow = _f[1];
    var _g = tslib_1.__read(react_1.useState(isLocked), 2), isLockedNow = _g[0], setLockedNow = _g[1];
    var onSharedGranteeDelete = react_1.useCallback(function (grantee) {
        setGranteesToDelete(function (state) { return tslib_1.__spread(state, [grantee]); });
    }, []);
    var onAddedGranteeDelete = react_1.useCallback(function (grantee) {
        setGranteesToAdd(function (state) { return state.filter(function (g) { return !sdk_model_1.areObjRefsEqual(g.id, grantee.id); }); });
    }, []);
    var onGranteeAdd = react_1.useCallback(function (grantee) {
        setGranteesToAdd(function (state) { return tslib_1.__spread(state, [grantee]); });
    }, []);
    var onAddGranteeButtonClick = react_1.useCallback(function () {
        setDialogMode("AddGrantee");
    }, []);
    var onAddGranteeBackClick = react_1.useCallback(function () {
        setDialogMode("ShareGrantee");
        setGranteesToAdd([]);
    }, []);
    var onLoadGrantees = react_1.useCallback(function (grantees, groupAll) {
        if (groupAll) {
            setGrantees(tslib_1.__spread(grantees, [groupAll]));
        }
        else {
            setGrantees(grantees);
        }
        setIsGranteesLoading(false);
    }, []);
    var onUnderLenientControlChange = react_1.useCallback(function (isUnderLenientControl) {
        setUnderLenientControlNow(isUnderLenientControl);
    }, []);
    var onLockChange = react_1.useCallback(function (isLocked) {
        setLockedNow(isLocked);
    }, []);
    return {
        dialogMode: dialogMode,
        isGranteesLoading: isGranteesLoading,
        grantees: grantees,
        granteesToAdd: granteesToAdd,
        granteesToDelete: granteesToDelete,
        onLoadGrantees: onLoadGrantees,
        onSharedGranteeDelete: onSharedGranteeDelete,
        onAddedGranteeDelete: onAddedGranteeDelete,
        onGranteeAdd: onGranteeAdd,
        onAddGranteeButtonClick: onAddGranteeButtonClick,
        onAddGranteeBackClick: onAddGranteeBackClick,
        isUnderLenientControlNow: isUnderLenientControlNow,
        isLockedNow: isLockedNow,
        onUnderLenientControlChange: onUnderLenientControlChange,
        onLockChange: onLockChange,
    };
};
/**
 * @internal
 */
exports.useShareDialogBase = function (props) {
    var sharedObject = props.sharedObject, currentUserRef = props.currentUserRef, onSubmit = props.onSubmit, onError = props.onError;
    var ref = sharedObject.ref, shareStatus = sharedObject.shareStatus, owner = sharedObject.owner, isUnderLenientControl = sharedObject.isUnderLenientControl, isLocked = sharedObject.isLocked;
    var _a = useShareDialogState(isUnderLenientControl, isLocked), dialogMode = _a.dialogMode, isGranteesLoading = _a.isGranteesLoading, isLockedNow = _a.isLockedNow, isUnderLenientControlNow = _a.isUnderLenientControlNow, grantees = _a.grantees, granteesToAdd = _a.granteesToAdd, granteesToDelete = _a.granteesToDelete, onLoadGrantees = _a.onLoadGrantees, onSharedGranteeDelete = _a.onSharedGranteeDelete, onAddedGranteeDelete = _a.onAddedGranteeDelete, onGranteeAdd = _a.onGranteeAdd, onAddGranteeButtonClick = _a.onAddGranteeButtonClick, onAddGranteeBackClick = _a.onAddGranteeBackClick, onLockChange = _a.onLockChange, onUnderLenientControlChange = _a.onUnderLenientControlChange;
    var onLoadGranteesSuccess = react_1.useCallback(function (result) {
        var groupAll = shareDialogMappers_1.mapShareStatusToGroupAll(shareStatus);
        onLoadGrantees(result, groupAll);
    }, [onLoadGrantees, shareStatus]);
    useGetAccessList_1.useGetAccessList({ currentUserRef: currentUserRef, sharedObjectRef: ref, onSuccess: onLoadGranteesSuccess, onError: onError });
    var isShareDialogDirty = react_1.useMemo(function () {
        return (granteesToDelete.length !== 0 ||
            isLocked !== isLockedNow ||
            isUnderLenientControl !== isUnderLenientControlNow);
    }, [granteesToDelete, isLocked, isLockedNow, isUnderLenientControl, isUnderLenientControlNow]);
    var isAddDialogDirty = react_1.useMemo(function () {
        return granteesToAdd.length !== 0;
    }, [granteesToDelete, granteesToAdd]);
    var onSubmitShareGrantee = react_1.useCallback(function () {
        if (!isShareDialogDirty) {
            return;
        }
        onSubmit(grantees, granteesToAdd, granteesToDelete, isUnderLenientControlNow, isLockedNow);
    }, [
        grantees,
        granteesToAdd,
        granteesToDelete,
        isShareDialogDirty,
        isUnderLenientControlNow,
        isLockedNow,
        onSubmit,
    ]);
    var onSubmitAddGrantee = react_1.useCallback(function () {
        if (!isAddDialogDirty) {
            return;
        }
        onSubmit(grantees, granteesToAdd, granteesToDelete, isUnderLenientControlNow, isLockedNow);
    }, [
        grantees,
        granteesToAdd,
        granteesToDelete,
        isAddDialogDirty,
        isUnderLenientControlNow,
        isLockedNow,
        onSubmit,
    ]);
    var sharedGrantees = react_1.useMemo(function () {
        return utils_1.notInArrayFilter(grantees, granteesToDelete);
    }, [grantees, granteesToDelete]);
    var appliedGranteesWithOwner = react_1.useMemo(function () {
        var appliedGrantees = utils_1.getAppliedGrantees(grantees, granteesToAdd, granteesToDelete);
        if (types_1.isGranteeUserInactive(owner)) {
            return appliedGrantees;
        }
        return tslib_1.__spread(appliedGrantees, [owner]);
    }, [grantees, granteesToDelete, granteesToAdd]);
    return {
        onAddedGranteeDelete: onAddedGranteeDelete,
        onSharedGranteeDelete: onSharedGranteeDelete,
        onAddGranteeBackClick: onAddGranteeBackClick,
        onAddGranteeButtonClick: onAddGranteeButtonClick,
        onGranteeAdd: onGranteeAdd,
        onSubmitShareGrantee: onSubmitShareGrantee,
        onSubmitAddGrantee: onSubmitAddGrantee,
        isGranteesLoading: isGranteesLoading,
        granteesToAdd: granteesToAdd,
        dialogMode: dialogMode,
        isShareDialogDirty: isShareDialogDirty,
        isAddDialogDirty: isAddDialogDirty,
        sharedGrantees: sharedGrantees,
        appliedGranteesWithOwner: appliedGranteesWithOwner,
        onLockChange: onLockChange,
        onUnderLenientControlChange: onUnderLenientControlChange,
        isUnderLenientControlNow: isUnderLenientControlNow,
        isLockedNow: isLockedNow,
    };
};
//# sourceMappingURL=useShareDialogBase.js.map